import React, { useState } from 'react';
import UserCreationForm from './UserCreationForm';
import BoatCreationForm from './BoatCreationForm';
import DocumentViewerModal from '../Document/DocumentViewerModal';
import AnalyticsDashboard from './AnalyticsDashboard';

const AdminDashboard = ({ users, onAddScan, onCreateUser, onCreateBoat, onDeleteUser, onDeleteBoat, onEditScan, onDeleteDocument, onAddDocumentToScan, currentUserRole, onChangeUserRole, onEditUserEmail }) => {
  const [activeTab, setActiveTab] = useState('scans');
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedBoat, setSelectedBoat] = useState(null);
  const [selectedScan, setSelectedScan] = useState(null);
  const [newScan, setNewScan] = useState({ date: '', embedCode: '', documents: [] });
  const [newDocument, setNewDocument] = useState({ name: '', type: 'pdf', url: '' });
  const [editingScan, setEditingScan] = useState(null);
  const [newDocumentForScan, setNewDocumentForScan] = useState({ name: '', type: 'pdf', url: '' });
  const [viewingDocument, setViewingDocument] = useState(null);
  const [copiedScanLink, setCopiedScanLink] = useState(null);
  const [editingUserEmail, setEditingUserEmail] = useState(null);


  const handleAddScan = () => {
    if (selectedBoat && newScan.date && newScan.embedCode) {
      onAddScan(selectedUser.id, selectedBoat.id, newScan);
      setNewScan({ date: '', embedCode: '', documents: [] });
    }
  };

  const handleAddDocumentToNewScan = () => {
    if (newDocument.name && newDocument.url) {
      setNewScan(prevScan => ({
        ...prevScan,
        documents: [...prevScan.documents, { id: Date.now(), ...newDocument }]
      }));
      setNewDocument({ name: '', type: 'pdf', url: '' });
    }
  };


  const handleDeleteUser = (userId) => {
    if (window.confirm('¿Estás seguro de eliminar este usuario?')) {
      onDeleteUser(userId);
    }
  };

  const handleDeleteBoat = (userId, boatId) => {
    if (window.confirm('¿Estás seguro de eliminar esta embarcación?')) {
      onDeleteBoat(userId, boatId);
      setSelectedBoat(null);
    }
  };

  const handleEditScan = () => {
    if (editingScan) {
      onEditScan(selectedUser.id, selectedBoat.id, editingScan.id, editingScan);
      setEditingScan(null);
    }
  };

  const handleDeleteDocument = (documentId) => {
    if (window.confirm('¿Estás seguro de eliminar este documento?')) {
      onDeleteDocument(selectedUser.id, selectedBoat.id, selectedScan.id, documentId);
      setEditingScan(prevScan => ({
        ...prevScan,
        documents: prevScan.documents.filter(doc => doc.id !== documentId)
      }));
    }
  };

  const handleAddDocumentToScan = () => {
    if (selectedScan && newDocumentForScan.name && newDocumentForScan.url) {
      onAddDocumentToScan(selectedUser.id, selectedBoat.id, selectedScan.id, newDocumentForScan);
      setEditingScan(prevScan => ({
        ...prevScan,
        documents: [...prevScan.documents, { id: Date.now(), ...newDocumentForScan }]
      }));
      setNewDocumentForScan({ name: '', type: 'pdf', url: '' });
    }
  };

  const handleSelectDocument = (document) => {
    setViewingDocument(document);
  };

  const handleCloseDocumentViewer = () => {
    setViewingDocument(null);
  };

  const handleCopyScanLink = (scanId) => {
    const link = `${window.location.origin}?scanId=${scanId}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopiedScanLink(scanId);
      setTimeout(() => setCopiedScanLink(null), 2000);
    }).catch(err => {
      console.error('Error copying link: ', err);
    });
  };

  const handleRoleChange = (userId, newRole) => {
    onChangeUserRole(userId, newRole);
  };

  const handleEditEmail = (user) => {
    setEditingUserEmail({ id: user.id, email: user.email });
  };

  const handleSaveEmail = () => {
    if (editingUserEmail && editingUserEmail.email) {
      onEditUserEmail(editingUserEmail.id, editingUserEmail.email);
      setEditingUserEmail(null);
    }
  };

  const handleCancelEditEmail = () => {
    setEditingUserEmail(null);
  };


  const getIcon = (type) => {
    switch (type) {
      case 'pdf':
        return (
          <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        );
      case 'dwg':
        return (
          <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
    }
  };


  return (
    <div className="space-y-8">
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab('scans')}
          className={`px-4 py-2 font-medium ${activeTab === 'scans' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
        >
          Gestión de Escaneos
        </button>
        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-2 font-medium ${activeTab === 'users' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
        >
          Gestión de Usuarios
        </button>
        <button
          onClick={() => setActiveTab('boats')}
          className={`px-4 py-2 font-medium ${activeTab === 'boats' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
        >
          Gestión de Embarcaciones
        </button>
         <button
          onClick={() => setActiveTab('analytics')}
          className={`px-4 py-2 font-medium ${activeTab === 'analytics' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
        >
          Analíticas
        </button>
      </div>

      {activeTab === 'scans' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h3 className="text-lg font-medium mb-4">Seleccionar Usuario y Embarcación</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Usuario</label>
                <select
                  onChange={(e) => {
                    const user = users.find(u => u.id === parseInt(e.target.value));
                    setSelectedUser(user);
                    setSelectedBoat(null);
                    setSelectedScan(null);
                    setEditingScan(null);
                  }}
                  className="w-full border border-gray-300 rounded-md p-2"
                >
                  <option value="">Seleccionar usuario</option>
                  {users.filter(u => u.role === 'user').map(user => (
                    <option key={user.id} value={user.id}>{user.username}</option>
                  ))}
                </select>
              </div>

              {selectedUser && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Embarcación</label>
                  <select
                    onChange={(e) => {
                      const boat = selectedUser.boats.find(b => b.id === parseInt(e.target.value));
                      setSelectedBoat(boat);
                      setSelectedScan(null);
                      setEditingScan(null);
                    }}
                    className="w-full border border-gray-300 rounded-md p-2"
                  >
                    <option value="">Seleccionar embarcación</option>
                    {selectedUser.boats.map(boat => (
                      <option key={boat.id} value={boat.id}>{boat.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {selectedBoat && (
                <div className="mt-4">
                  <h4 className="text-md font-medium text-gray-800 mb-2">Escaneos de {selectedBoat.name}</h4>
                  <ul className="space-y-2">
                    {selectedBoat.scans.map(scan => (
                      <li
                        key={scan.id}
                        onClick={() => {
                          setSelectedScan(scan);
                          setEditingScan({...scan});
                        }}
                        className={`p-2 border rounded-md cursor-pointer ${selectedScan?.id === scan.id ? 'bg-blue-100 border-blue-500' : 'border-gray-200 hover:bg-gray-50'}`}
                      >
                        <div className="flex justify-between items-center">
                           <span>Escaneo del {scan.date}</span>
                           <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopyScanLink(scan.id);
                              }}
                              className="text-blue-600 hover:text-blue-800 text-sm ml-2"
                            >
                              {copiedScanLink === scan.id ? '¡Copiado!' : 'Compartir'}
                            </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {selectedScan && editingScan && (
            <div className="bg-white p-6 rounded-lg shadow-sm space-y-6">
              <h3 className="text-lg font-medium mb-4">Editar Escaneo del {selectedScan.date}</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha</label>
                <input
                  type="date"
                  value={editingScan.date}
                  onChange={(e) => setEditingScan({...editingScan, date: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Código Embed</label>
                <textarea
                  value={editingScan.embedCode}
                  onChange={(e) => setEditingScan({...editingScan, embedCode: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 h-24"
                  placeholder="<iframe src='...' />"
                />
              </div>

              <h4 className="text-md font-medium text-gray-800 mb-2">Documentos</h4>
              <div className="space-y-3">
                {editingScan.documents.map(doc => (
                  <div key={doc.id} className="flex items-center justify-between p-2 border border-gray-200 rounded-md">
                    <div className="flex items-center">
                      {getIcon(doc.type)}
                      <span className="ml-2 text-sm text-gray-700">{doc.name}</span>
                    </div>
                    <button
                      onClick={() => handleDeleteDocument(doc.id)}
                      className="text-red-600 hover:text-red-900 text-sm"
                    >
                      Eliminar
                    </button>
                  </div>
                ))}
              </div>

              <div className="space-y-4 border-t border-gray-200 pt-4">
                 <h4 className="text-md font-medium text-gray-800 mb-2">Agregar Nuevo Documento a este Escaneo</h4>
                 <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                    <input
                      type="text"
                      value={newDocumentForScan.name}
                      onChange={(e) => setNewDocumentForScan({...newDocumentForScan, name: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                    <select
                      value={newDocumentForScan.type}
                      onChange={(e) => setNewDocumentForScan({...newDocumentForScan, type: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="pdf">PDF</option>
                      <option value="dwg">DWG</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
                    <input
                      type="text"
                      value={newDocumentForScan.url}
                      onChange={(e) => setNewDocumentForScan({...newDocumentForScan, url: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <button
                    onClick={handleAddDocumentToScan}
                    className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition-colors"
                  >
                    Agregar Documento
                  </button>
              </div>
              
              <button
                onClick={handleEditScan}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
              >
                Guardar Cambios del Escaneo
              </button>
            </div>
          )}

          {!selectedScan && selectedBoat && (
             <>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-medium mb-4">Agregar Nuevo Escaneo</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fecha</label>
                    <input
                      type="date"
                      value={newScan.date}
                      onChange={(e) => setNewScan({...newScan, date: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Código Embed</label>
                    <textarea
                      value={newScan.embedCode}
                      onChange={(e) => setNewScan({...newScan, embedCode: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 h-24"
                      placeholder="<iframe src='...' />"
                    />
                  </div>

                  <h4 className="text-md font-medium text-gray-800 mb-2">Documentos para este Escaneo</h4>
                  <div className="space-y-3">
                    {newScan.documents.map(doc => (
                      <div key={doc.id} className="flex items-center justify-between p-2 border border-gray-200 rounded-md">
                        <div className="flex items-center">
                          {getIcon(doc.type)}
                          <span className="ml-2 text-sm text-gray-700">{doc.name}</span>
                        </div>
                        {/* Implementar eliminación de documentos antes de agregar el escaneo */}
                      </div>
                    ))}
                  </div>

                  <div className="space-y-4 border-t border-gray-200 pt-4">
                    <h4 className="text-md font-medium text-gray-800 mb-2">Agregar Documento al Nuevo Escaneo</h4>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                      <input
                        type="text"
                        value={newDocument.name}
                        onChange={(e) => setNewDocument({...newDocument, name: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                      <select
                        value={newDocument.type}
                        onChange={(e) => setNewDocument({...newDocument, type: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="pdf">PDF</option>
                        <option value="dwg">DWG</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
                      <input
                        type="text"
                        value={newDocument.url}
                        onChange={(e) => setNewDocument({...newDocument, url: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <button
                    onClick={handleAddDocumentToNewScan}
                    className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition-colors"
                  >
                    Agregar Documento a este Escaneo
                  </button>
                </div>
                  
                  <button
                    onClick={handleAddScan}
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Agregar Escaneo
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      ) : activeTab === 'users' ? (
        <div className="grid grid-cols-1 gap-6">
          <UserCreationForm onCreateUser={onCreateUser} currentUserRole={currentUserRole} />
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h3 className="text-lg font-medium mb-4">Lista de Usuarios</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuario</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Correo</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rol</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.filter(u => u.role !== 'masteradmin' || currentUserRole === 'masteradmin').map(user => (
                    <tr key={user.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{user.username}</td>
                       <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                         {editingUserEmail?.id === user.id ? (
                           <input
                             type="email"
                             value={editingUserEmail.email}
                             onChange={(e) => setEditingUserEmail({...editingUserEmail, email: e.target.value})}
                             className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:ring-blue-500 focus:border-blue-500"
                           />
                         ) : (
                           user.email
                         )}
                       </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${user.role === 'admin' ? 'bg-purple-100 text-purple-800' : user.role === 'masteradmin' ? 'bg-red-100 text-red-800' : user.role === 'guest' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                          {user.role === 'admin' ? 'Administrador' : user.role === 'masteradmin' ? 'Admin Maestro' : user.role === 'guest' ? 'Invitado' : 'Usuario'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {user.role !== 'masteradmin' && (
                          <div className="flex justify-end space-x-2">
                             {currentUserRole === 'masteradmin' && (
                               <>
                                 {editingUserEmail?.id === user.id ? (
                                   <>
                                     <button
                                       onClick={handleSaveEmail}
                                       className="text-green-600 hover:text-green-900"
                                     >
                                       Guardar
                                     </button>
                                     <button
                                       onClick={handleCancelEditEmail}
                                       className="text-gray-600 hover:text-gray-900"
                                     >
                                       Cancelar
                                     </button>
                                   </>
                                 ) : (
                                   <button
                                     onClick={() => handleEditEmail(user)}
                                     className="text-blue-600 hover:text-blue-900"
                                   >
                                     Editar Correo
                                   </button>
                                 )}
                               </>
                             )}
                            {currentUserRole === 'masteradmin' && user.role === 'guest' && (
                               <button
                                  onClick={() => handleRoleChange(user.id, 'user')}
                                  className="text-blue-600 hover:text-blue-900"
                                >
                                  Convertir a Usuario
                                </button>
                            )}
                             {currentUserRole === 'masteradmin' && user.role === 'user' && (
                               <button
                                  onClick={() => handleRoleChange(user.id, 'guest')}
                                  className="text-yellow-600 hover:text-yellow-900"
                                >
                                  Convertir a Invitado
                                </button>
                            )}
                            <button
                              onClick={() => handleDeleteUser(user.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Eliminar
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : activeTab === 'boats' ? (
        <div className="grid grid-cols-1 gap-6">
          <BoatCreationForm onCreateBoat={onCreateBoat} users={users} />
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h3 className="text-lg font-medium mb-4">Lista de Embarcaciones por Usuario</h3>
            <div className="space-y-4">
              {users.filter(u => u.role === 'user').map(user => (
                <div key={user.id} className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-2">{user.username}</h4>
                  <ul className="space-y-2 text-gray-600">
                    {user.boats.length > 0 ? (
                      user.boats.map(boat => (
                        <li key={boat.id} className="flex justify-between items-center">
                          <span>{boat.name} ({boat.scans.length} escaneos)</span>
                          <button
                            onClick={() => handleDeleteBoat(user.id, boat.id)}
                            className="text-red-600 hover:text-red-900 text-sm"
                          >
                            Eliminar
                          </button>
                        </li>
                      ))
                    ) : (
                      <li>No hay embarcaciones asignadas.</li>
                    )}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : ( // activeTab === 'analytics'
         <AnalyticsDashboard users={users} />
      )}

      <DocumentViewerModal document={viewingDocument} onClose={handleCloseDocumentViewer} />
    </div>
  );
};

export default AdminDashboard;